import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { AUTH_ROUTE, HOME_ROUTE } from '@/constants/routes';
import { googleCallback } from '@/api/auth';
import { syncUserFromServer } from '@/api/user';
import { saveToken } from '@/stores/secure';
import { useAuthStore } from '@/stores/AuthState';
import { useTranslation } from '@/utils/i18n';
import { getOAuthParam } from '@/utils/oauthUtils';
import { PrimaryButton } from '@/components/Buttons';
import { RacoreText } from '@/components/Logos';
import type { GoogleTokens } from '@/types';

/**
 * OAuth callback landing — receives tokens from Strapi's provider redirect
 * (replaces the legacy `racore://` deep link). Exchanges them for a Racore
 * JWT, persists it, hydrates the user, and routes to /home.
 */
export const Route = createFileRoute('/auth/callback')({
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const setUserInfo = useAuthStore((s) => s.setUserInfo);
  const language = useAuthStore((s) => s.userInfo.language) || 'fr';
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const finish = async () => {
      const url = window.location.href;
      const tokens: GoogleTokens = {
        access_token: getOAuthParam(url, 'access_token') ?? undefined,
        refresh_token: getOAuthParam(url, 'refresh_token') ?? undefined,
        id_token: getOAuthParam(url, 'id_token') ?? undefined,
      };

      if (!tokens.access_token && !tokens.id_token) {
        if (!cancelled) setError(t('login.error.missingTokens'));
        return;
      }

      try {
        const response = await googleCallback(tokens, language);
        await saveToken(response.jwt, response.refreshToken);
        // Google is the auth provider, not a music service — service stays unset.
        await setUserInfo(response.user as never);
        // profile-sync-on-launch — the OAuth payload carries no favourite
        // service: re-read the full profile now that the session exists.
        void syncUserFromServer();
        if (!cancelled) navigate({ to: HOME_ROUTE, replace: true });
      } catch {
        if (!cancelled) setError(t('login.error.authenticationFailed'));
      }
    };

    void finish();
    return () => {
      cancelled = true;
    };
  }, [language, navigate, setUserInfo, t]);

  return (
    <div
      className="tw:flex tw:h-full tw:flex-col tw:items-center tw:justify-center tw:gap-6 tw:px-5"
      data-testid="screen-auth-callback"
    >
      <RacoreText width={150} height={31} />

      {error ? (
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-5">
          <p className="tw:max-w-[300px] tw:text-base tw:text-center tw:text-white/90">{error}</p>
          <div className="tw:w-[220px]">
            <PrimaryButton
              label={t('login.error.goBack')}
              onClick={() => navigate({ to: AUTH_ROUTE, replace: true })}
              data-testid="btn-callback-back"
            />
          </div>
        </div>
      ) : (
        <div className="tw:flex tw:items-center tw:gap-3 tw:text-white/70">
          <span className="tw:size-5 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/30 tw:border-t-white" />
          <p className="tw:text-base">{t('login.welcomeMessage')}</p>
        </div>
      )}
    </div>
  );
}
